import React, { useEffect, useState } from "react"
import { Card, Button } from "react-bootstrap"
import { useNavigate } from "react-router-dom"
import Layout from "../../../layout/default"
import Block from "../../../components/Block/Block"
import DataTable from "../../../components/DataTable/DataTable"
import subscriptionService from "../services/subscriptionService"
import { Icon } from "../../../components"

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—")

const SubscriptionList = () => {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const navigate = useNavigate()

  /* ───────── acciones ──────────────────────────── */
  const handleEdit = (id) => navigate(`/subscription/edit/${id}`)
  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar suscripción?")) return
    setLoading(true)
    try {
      await subscriptionService.remove(id)
      setRows((prev) => prev.filter((r) => r.id !== id))
    } catch (err) {
      console.error("Error deleting subscription", err)
      setError("No se pudo eliminar la suscripción.")
    } finally {
      setLoading(false)
    }
  }

  /* ───────── fetch ─────────────────────────────── */
  useEffect(() => {
    let mounted = true
    ;(async () => {
      setLoading(true)
      setError(null)
      try {
        const res = await subscriptionService.list()
        const data = Array.isArray(res)
          ? res
          : Array.isArray(res.results)
          ? res.results
          : Array.isArray(res.data)
          ? res.data
          : []
        if (mounted) setRows(data)
      } catch (err) {
        console.error("Error fetching subscriptions", err)
        if (mounted) setError("No se pudo cargar las suscripciones.")
      } finally {
        if (mounted) setLoading(false)
      }
    })()
    return () => {
      mounted = false
    }
  }, [])

  /* ───────── columnas ──────────────────────────── */
  const cols = [
    {
      name: "ID",
      selector: (r) => r.id,
    },
    {
      name: "Abogado",
      selector: (r) => r.lawyer_name + " " + r.lawyer_last_name,
      sortable: true,
    },
    { name: "Plan", selector: (r) => r.plan_name, sortable: true },
    { name: "Ciclo", selector: (r) => r.cycle_name, sortable: true },
    { name: "Inicio", selector: (r) => fmtDate(r.started_at), sortable: true },
    {
      name: "Próx. cobro",
      selector: (r) => fmtDate(r.next_payment_at),
      sortable: true,
    },
    {
      name: "Últ. cobro",
      selector: (r) => fmtDate(r.last_payment_at),
      sortable: true,
    },
    {
      name: "Subtotal €",
      selector: (r) => r.subtotal?.toFixed?.(2) ?? r.subtotal,
      sortable: true,
    },
    {
      name: "Total €",
      selector: (r) => r.grand_total?.toFixed?.(2) ?? r.grand_total,
      sortable: true,
    },
    { name: "Método", selector: (r) => r.payment_method_name, sortable: true },
    {
      name: "Activo",
      selector: (r) => (r.is_active ? "Sí" : "No"),
      sortable: true,
    },
    {
      name: "Acciones",
      fixed: "right",
      cell: (row) => (
        <>
          <Button
            size="sm"
            variant="outline-primary"
            className="me-1"
            onClick={() => handleEdit(row.id)}
          >
            <Icon name="edit" />
          </Button>
          <Button
            size="sm"
            variant="outline-danger"
            onClick={() => handleDelete(row.id)}
          >
            <Icon name="trash" />
          </Button>
        </>
      ),
    },
  ]
  const fmtDate = (d, long = false) => {
    if (!d) return "—"

    /*
     *  long  = "10 de julio de 2025"
     *  corto = "10 jul 2025"
     */
    const opts = long
      ? { day: "2-digit", month: "long", year: "numeric" }
      : { day: "2-digit", month: "short", year: "numeric" }

    return new Date(d).toLocaleDateString("es-ES", opts)
  }

  /* ───────── render ─────────────────────────────── */
  return (
    <Layout title="Suscripciones" content="container" loading={loading}>
      <Block.Head>
        <Block.HeadBetween>
          <Block.HeadContent>
            <Block.Title tag="h2">Suscripciones</Block.Title>
          </Block.HeadContent>
          <Block.HeadContent>
            <Button
              variant="primary"
              onClick={() => navigate("/subscription/new")}
            >
              <Icon name="plus" />
              <span> Nueva suscripción</span>
            </Button>
          </Block.HeadContent>
        </Block.HeadBetween>
      </Block.Head>

      <Block>
        <Card body>
          {error && <div className="mb-3 text-danger">{error}</div>}

          <DataTable
            data={rows}
            columns={cols}
            progressPending={loading}
            noDataComponent={<div>No hay registros.</div>}
            pagination
            highlightOnHover
            searchByList={[
              "lawyer_name",
              "plan_name",
              "payment_method_name",
              "cycle_name",
            ]}
            className="data-table-head-light"
          />
        </Card>
      </Block>
    </Layout>
  )
}

export default SubscriptionList
